import { AppImageIdentifier, AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import {
	Device,
	OperatingSystem,
	Browser,
	IpAddressRange,
	PermissionSetModel
} from "client/Api/common";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { ObjectWithClientId } from "client/Misc/clientId";
import {
	AppColorViewModel,
	AppType,
	CalendarDestinationApp,
	ClientlessRdpDestinationWindow,
	DestinationWindow,
	EmailDestinationApp,
	SaveAppColorModel
} from "../common";
import { Guid } from "client_legacy/ndaw/guid";
import { RssFeedType } from "client/Api/Workspace/LiveInformation/Rss/rssLiveInformation";
import { environmentHttpClient } from "client/Api/httpClient";
import { TagSaveModel, TagViewModel } from "client/Api/Tags/tags";

export interface ConditionalAccessSettingsModel {
	hideTilesWhenNotAccessible: boolean;
	devices: readonly Device[] | null;
	operatingSystems: readonly OperatingSystem[] | null;
	browsers: readonly Browser[] | null;
	whitelistIpRanges: readonly string[] | null;
	blacklistIpRanges: readonly string[] | null;
}

export interface SaveAppConditionalAccessSettingsModel {
	hideTilesWhenNotAccessible: boolean;
	restrictDevices: boolean;
	devices: readonly Device[];
	restrictOperatingSystems: boolean;
	operatingSystems: readonly OperatingSystem[];
	restrictBrowsers: boolean;
	browsers: readonly Browser[];
	restrictIp: boolean;
	whitelistIpRanges: readonly ObjectWithClientId<IpAddressRange>[];
	blacklistIpRanges: readonly ObjectWithClientId<IpAddressRange>[];
}

export interface MaintenanceSettingsInitializationModel {
	isMaintenanceEnabled: boolean;
	startsOn: string;
	endsOn: string;
	notificationMessage: string;
}

export interface SaveMaintenanceSettingsModel {
	isMaintenanceEnabled: boolean;
	startsOn: Date | null;
	endsOn: Date | null;
	notificationMessage: string | null;
}

export interface EditGenericAppViewModel {
	id: number;
	name: string;
	color: AppColorViewModel;
	image: AppImageIdentifier;
	type: AppType;
	imageDisplayInfo: AppImageDisplayInfo;
	userLicense: UserLicense;
	inheritedColor: AppColorViewModel | null;
	isConnectedToMainApp: boolean;
	canBeConfigured: boolean;
	everyoneHasAccess: boolean;
	whoHasAccess: PermissionSetModel;
	owners: PermissionSetModel;
	tags: readonly TagViewModel[];
}

export interface EditAppModel<TAppModel extends EditGenericAppViewModel> {
	app: TAppModel;
	hasLinkNavigationSupport: boolean;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
}

export enum SaveAppTargetType {
	ExistingInstalledApp = 1,
	NewEnvironmentOnlyApp = 2,
	NewAppFromMain = 3
}

export type SaveAppTargetModel =
	{
		type: SaveAppTargetType.ExistingInstalledApp;
		installedAppId: number;
	}
	|
	{
		type: SaveAppTargetType.NewAppFromMain;
		mainAppId: number;
	}
	|
	{
		type: SaveAppTargetType.NewEnvironmentOnlyApp;
	};

export interface SaveGenericAppModel {
	target: SaveAppTargetModel;
	color: SaveAppColorModel;
	image: AppImageIdentifier | null;
	name: string;
	everyoneHasAccess: boolean;
	whoHasAccess: PermissionSetModel | null;
	owners: PermissionSetModel;
	tags: readonly TagSaveModel[];
}

export function saveGenericApp(
	model: SaveGenericAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveGenericApp",
		model,
		{ signal }
	);
}

export interface EditAfasAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export interface SaveAfasAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export function saveAfasApp(
	model: SaveAfasAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveAfasApp",
		model,
		{ signal }
	);
}

export type AutotaskConnectionIdentifierViewModel = {
	id: number | null;
};

export interface EditAutotaskAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connection: AutotaskConnectionIdentifierViewModel;
}

export interface SaveAutotaskAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connection: AutotaskConnectionIdentifierViewModel | null;
}

export function saveAutotaskApp(
	model: SaveAutotaskAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveAutotaskApp",
		model,
		{ signal }
	);
}

export interface EditAzureAppViewModel extends EditGenericAppViewModel {
	urlSuffix: string;
	destinationWindow: DestinationWindow;
	maintenanceWindow: MaintenanceSettingsInitializationModel;
	conditionalAccessSettings: ConditionalAccessSettingsModel;
	urlPrefix: string;
}

export interface SaveAzureAppAppModel extends SaveGenericAppModel {
	urlSuffix: string | null;
	destinationWindow: DestinationWindow;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveAzureApp(
	model: SaveAzureAppAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveAzureApp",
		model,
		{ signal }
	);
}

export enum BirthdayScopeKind {
	Everyone = 1,
	Department = 2
}

export interface EditBirthdayAppViewModel extends EditGenericAppViewModel {
	scope: BirthdayScopeKind;
}

export interface SaveBirthdayAppModel extends SaveGenericAppModel {
	scope: BirthdayScopeKind;
}

export function saveBirthdayApp(
	model: SaveBirthdayAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveBirthdayApp",
		model,
		{ signal }
	);
}

export interface EditCalendarAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	destinationApp: CalendarDestinationApp;
}

export interface SaveCalendarAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	destinationApp: CalendarDestinationApp | null;
}

export function saveCalendarApp(
	model: SaveCalendarAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveCalendarApp",
		model,
		{ signal }
	);
}

export interface EditCitrixXenAppViewModel extends EditGenericAppViewModel {
	serverUrl: string;
	appName: string;
	maintenanceWindow: MaintenanceSettingsInitializationModel;
	conditionalAccessSettings: ConditionalAccessSettingsModel;
}

export interface SaveCitrixXenAppModel extends SaveGenericAppModel {
	serverUrl: string | null;
	appName: string | null;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveCitrixXenAppApp(
	model: SaveCitrixXenAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveCitrixXenAppApp",
		model,
		{ signal }
	);
}

export interface EditClientlessRdpAppViewModel extends EditGenericAppViewModel {
	gatewayId: number;
	connectionId: string;
	destinationWindow: ClientlessRdpDestinationWindow;
	maintenanceWindow: MaintenanceSettingsInitializationModel;
	conditionalAccessSettings: ConditionalAccessSettingsModel;
}

export interface SaveClientlessRdpAppModel extends SaveGenericAppModel {
	gatewayId: number | null;
	connectionId: string | null;
	destinationWindow: ClientlessRdpDestinationWindow;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveClientlessRdpApp(
	model: SaveClientlessRdpAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveClientlessRdpApp",
		model,
		{ signal }
	);
}

export interface EditConnectWiseAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
}

export interface SaveConnectWiseAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
}

export function saveConnectWiseApp(
	model: SaveConnectWiseAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveConnectWiseApp",
		model,
		{ signal }
	);
}

export interface EditDocumentsAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
}

export interface SaveDocumentsAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
}

export function saveDocumentsApp(
	model: SaveDocumentsAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveDocumentsApp",
		model,
		{ signal }
	);
}

export interface EditEmailAppViewModel extends EditGenericAppViewModel {
	destinationApp: EmailDestinationApp;
	destinationWindow: DestinationWindow;
}

export interface SaveEmailAppModel extends SaveGenericAppModel {
	destinationApp: EmailDestinationApp | null;
	destinationWindow: DestinationWindow;
}

export function saveEmailApp(
	model: SaveEmailAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveEmailApp",
		model,
		{ signal }
	);
}

export enum EmbedAppContentKind {
	EmbedCode = 1,
	CustomHtml = 2
}

export interface EditEmbedAppViewModel extends EditGenericAppViewModel {
	html: string;
	navigationUrl: string;
	contentKind: EmbedAppContentKind;
	hideHeader: boolean;
	hideFooter: boolean;
	maintenanceWindow: MaintenanceSettingsInitializationModel;
	conditionalAccessSettings: ConditionalAccessSettingsModel;
}

export interface SaveEmbedAppModel extends SaveGenericAppModel {
	name: string;
	html: string | null;
	navigationUrl: string | null;
	contentKind: EmbedAppContentKind;
	hideHeader: boolean;
	hideFooter: boolean;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveEmbedApp(
	model: SaveEmbedAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveEmbedApp",
		model,
		{ signal }
	);
}

export interface EditGoogleAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
}

export interface SaveGoogleAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
}

export function saveGoogleApp(
	model: SaveGoogleAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveGoogleApp",
		model,
		{ signal }
	);
}

export interface EditLiquitAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export interface SaveLiquitAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export function saveLiquitApp(
	model: SaveLiquitAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveLiquitApp",
		model,
		{ signal }
	);
}

export interface EditLocalAppViewModel extends EditGenericAppViewModel {
	instructionId: Guid;
	maintenanceWindow: MaintenanceSettingsInitializationModel;
	conditionalAccessSettings: ConditionalAccessSettingsModel;
}

export interface SaveLocalAppAppModel extends SaveGenericAppModel {
	instructionId: Guid;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveLocalApp(
	model: SaveLocalAppAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveLocalApp",
		model,
		{ signal }
	);
}

export interface EditMagisterAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export interface SaveMagisterAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export function saveMagisterApp(
	model: SaveMagisterAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveMagisterApp",
		model,
		{ signal }
	);
}

export type NedapOnsConnectionIdentifierViewModel = {
	id: number;
};

export interface EditNedapOnsAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connection: NedapOnsConnectionIdentifierViewModel;
}

export interface SaveNedapOnsAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connection: NedapOnsConnectionIdentifierViewModel | null;
}

export function saveNedapOnsApp(
	model: SaveNedapOnsAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveNedapOnsApp",
		model,
		{ signal }
	);
}

export interface PredefinedRssFeedModel {
	id: number | null;
	name: string;
	feedUrl: string;
	feedType: RssFeedType;
	siteUrl: string;
}

export interface EditRssAppViewModel extends EditGenericAppViewModel {
	feeds: PredefinedRssFeedModel[];
}

export interface SaveRssAppModel extends SaveGenericAppModel {
	feeds: PredefinedRssFeedModel[];
}

export function saveRssApp(
	model: SaveRssAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveRssApp",
		model,
		{ signal }
	);
}

export enum SharePointAction {
	None = 0,
	OpenTeamSiteInSharePoint = 1,
	OpenInDocumentsApp = 2
}

export interface EditSharePointAppViewModel extends EditGenericAppViewModel {
	enabledActions: SharePointAction;
	destinationWindow: DestinationWindow;
}

export interface SaveSharePointAppModel extends SaveGenericAppModel {
	enabledActions: SharePointAction;
	destinationWindow: DestinationWindow;
}

export function saveSharePointApp(
	model: SaveSharePointAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveSharePointApp",
		model,
		{ signal }
	);
}

export interface EditShortcutAppViewModel extends EditGenericAppViewModel {
	uri: string;
	destinationWindow: DestinationWindow;
	maintenanceWindow: MaintenanceSettingsInitializationModel | null;
	conditionalAccessSettings: ConditionalAccessSettingsModel | null;
}

export interface SaveShortcutAppModel extends SaveGenericAppModel {
	uri: string | null;
	destinationWindow: DestinationWindow;
	maintenanceSettings: SaveMaintenanceSettingsModel | null;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel | null;
}

export function saveShortcutApp(
	model: SaveShortcutAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveShortcutApp",
		model,
		{ signal }
	);
}

export enum TeamsAction {
	None = 0,
	OpenWebClient = 0x1,
	OpenApp = 0x2,
	OpenLibraryInDocumentsApp = 0x4,
	All = OpenWebClient | OpenApp | OpenLibraryInDocumentsApp
}

export interface EditTeamsAppViewModel extends EditGenericAppViewModel {
	enabledActions: TeamsAction;
	destinationWindow: DestinationWindow;
}

export interface SaveTeamsAppModel extends SaveGenericAppModel {
	enabledActions: TeamsAction;
	destinationWindow: DestinationWindow;
}

export function saveTeamsApp(
	model: SaveTeamsAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveTeamsApp",
		model,
		{ signal }
	);
}

export interface EditToDoAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
}

export interface SaveToDoAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
}

export function saveToDoApp(
	model: SaveToDoAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveToDoApp",
		model,
		{ signal }
	);
}

export type TopDeskConnectionIdentifierViewModel = {
	id: number;
	isDefault: false;
} | {
	isDefault: true;
};

export interface EditTopDeskAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connection: TopDeskConnectionIdentifierViewModel;
}

export interface SaveTopDeskAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connection: TopDeskConnectionIdentifierViewModel | null;
}

export function saveTopDeskApp(
	model: SaveTopDeskAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveTopDeskApp",
		model,
		{ signal }
	);
}

export type VilansKickConnectionIdentifierViewModel = {
	id: number;
};

export interface EditVilansKickAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connection: VilansKickConnectionIdentifierViewModel;
}

export interface SaveVilansKickAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connection: VilansKickConnectionIdentifierViewModel | null;
}

export function saveVilansKickApp(
	model: SaveVilansKickAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveVilansKickApp",
		model,
		{ signal }
	);
}

export const enum ApplicationKind {
	Unknown = 0,
	Application = 1,
	Desktop = 2
}

export const enum ClientKind {
	Unknown = 0,
	Web = 1,
	NativeViaWeb = 2,
	Native = 3
}

export interface EditVmwareHorizonAppViewModel extends EditGenericAppViewModel {
	portalUrl: string | null;
	applicationName: string | null;
	applicationKind: ApplicationKind | null;
	clientKind: ClientKind | null;
	maintenanceWindow: MaintenanceSettingsInitializationModel;
	conditionalAccessSettings: ConditionalAccessSettingsModel;
}

export interface SaveVmwareHorizonAppAppModel extends SaveGenericAppModel {
	portalUrl: string | null;
	applicationName: string | null;
	applicationKind: ApplicationKind | null;
	clientKind: ClientKind | null;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveVmwareHorizonAppApp(
	model: SaveVmwareHorizonAppAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveVmwareHorizonAppApp",
		model,
		{ signal }
	);
}

export enum RemoteDesktopProtocolClientKind {
	MicrosoftTerminalServicesClient = 1,
	MicrosoftRemoteDesktopClient = 2,
	WindowsApp = 3,
	Web = 4
}

export enum WindowsVirtualDesktopVersionKind {
	NoneArm = 1,
	Arm = 2
}

export interface EditWindowsVirtualDesktopAppViewModel extends EditGenericAppViewModel {
	appName: string;
	client: RemoteDesktopProtocolClientKind;
	useMicrosoftTerminalServicesClientOnMacOs: boolean;
	versionKind: WindowsVirtualDesktopVersionKind | null;
	maintenanceWindow: MaintenanceSettingsInitializationModel;
	conditionalAccessSettings: ConditionalAccessSettingsModel;
}

export interface SaveWindowsVirtualDesktopAppModel extends SaveGenericAppModel {
	appName: string | null;
	client: RemoteDesktopProtocolClientKind | null;
	useMicrosoftTerminalServicesClientOnMacOs: boolean;
	versionKind: WindowsVirtualDesktopVersionKind | null;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveWindowsVirtualDesktopApp(
	model: SaveWindowsVirtualDesktopAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveWindowsVirtualDesktopApp",
		model,
		{ signal }
	);
}

export interface EditZaaksysteemAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export interface SaveZaaksysteemAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export function saveZaaksysteemApp(
	model: SaveZaaksysteemAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveZaaksysteemApp",
		model,
		{ signal }
	);
}

export interface EditZenyaDocAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export interface SaveZenyaDocAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export function saveZenyaDocApp(
	model: SaveZenyaDocAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveZenyaDocApp",
		model,
		{ signal }
	);
}

export interface EditZermeloAppViewModel extends EditGenericAppViewModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export interface SaveZermeloAppModel extends SaveGenericAppModel {
	destinationWindow: DestinationWindow;
	connectionId: number;
}

export function saveZermeloApp(
	model: SaveZermeloAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveZermeloApp",
		model,
		{ signal }
	);
}

export enum ConfigurableAddressBookAppContactSource {
	Personal = 1,
	Gal = 2
}

export interface EditAddressBookAppViewModel extends EditGenericAppViewModel {
	contactSources: readonly ConfigurableAddressBookAppContactSource[];
}

export interface SaveAddressBookAppModel extends SaveGenericAppModel {
	contactSources: readonly ConfigurableAddressBookAppContactSource[];
}

export function saveAddressBookApp(
	model: SaveAddressBookAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveAddressBookApp",
		model,
		{ signal }
	);
}

export interface EditIntegrationAppViewModel extends EditGenericAppViewModel {
	connectionId: number | null;
	navigationUrl: string | null;
	destinationWindow: DestinationWindow;
	maintenanceWindow: MaintenanceSettingsInitializationModel | null;
	conditionalAccessSettings: ConditionalAccessSettingsModel | null;
}

export interface SaveIntegrationAppModel extends SaveGenericAppModel {
	connectionId: number | null;
	navigationUrl: string | null;
	destinationWindow: DestinationWindow;
	maintenanceSettings: SaveMaintenanceSettingsModel;
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
}

export function saveIntegrationApp(
	model: SaveIntegrationAppModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"Apps/SaveIntegrationApp",
		model,
		{ signal }
	);
}